import { createSlice } from '@reduxjs/toolkit';
import { getDigitalEngagement, getProviderPage } from '../thunks/engagementThunks';
import { createDistrictUSAMapData, 
    generateDistinctHexColors, 
    getOverallPhysicians, 
    handlePeriodChange, 
    makeBarDataSeries, 
    makeLabels,
    removeEmptyNestedKeys,
    makeSeriesForDonutChart,
    createStateUSAMapData,
    calculateYmax,
    setTickAmount, 
    transformHospitalDataForBarChart,
    transformHospitalDataForMapChart, 
    customCategories, 
    sortDataByMonth } from '../../utility';
import { colors } from '../../constants';
import { createUsersAreaChartSeries } from '../../utility/webMatrixUtility';

const digitalEngagementInitialState = {
    isLoading: true,
    error: null,
    engagement: null,
    regionalEngagement: null,
    stateEngagement: null,
    distributionRegion: [],
    stateDistributionRegion: [],
    physicianEngagementSegmentsDonutChartData: [],
    physicianEngagementAffiliationTypeDonutChartData: [],
    districtBreakdownUSAMapData: [],
    stateBreakdownUSAMapData: [],
    distributionTerritories: [],
    territoriesBreakdownUSAMapData: [],
    engagementTotal: 0,
    isPeriodChangeIncrease: false,
    periodChange: 0,
    overallPhysicians: 0,
    overallTerritoriesPhysicians: 0,
    stateOverallPhysicians: 0,
    providers: [],
    filteredProviders: [],
    // '' means no search. Sent to the API rather than applied in the browser.
    searchTerm: '',
    // true once the API answers with a `providers_total`, i.e. it paginated for
    // us and the table must NOT slice the rows again.
    serverPaginated: false,
    isProvidersLoading: false,
    channelEngagement: {
        labels: [],
        series: [],
        colors: []
    },
    segmentEngagement: {
        labels: [],
        series: [],
        colors: []
    },
    clickEngagement: {
        labels: [],
        series: [],
        categoriesForAll: [],
        categories: [],
        actualData: {},
        selectedChannel: 'All',
        colors: [],
        yMax: 10,
        tickAmount: 5
    },
    currentPage: 1,
    pageSize: 10,
    totalRecordsFetch: 0,
    totalPages: 0,
    specialtyEngagement: [],
    decileEngagement: [],
    decileEngagementData: {
        labels: [],
        series: []
    },
    specialtyEngagementData: {
        labels: [],
        series: []
    },
    stateMapSeries: [],
    physicianEngagedBarChart: {
        categories: [],
        data: []
    },
    hospitalEngagementData: [],
    hospitalMapChart: [],
    hospitalBarChart: [],
    hospitalStateBarChart: [],
    overallHospitalPhysicians: 0,
    NpiEngagedChart: {
        labels: [],
        series: [],
        colors: []
    },
    repeatEngagementBarChart: {
        categories: [],
        data: []
    },
    hcpReachEngagement: [],
    // webmetrics data
    users_series_data: [],
    stateDistributionRegionPercentage: [],
    distributionRegionPercentage: []
};

const digitalEngagement = createSlice({
    name: 'digitalEngagement',
    initialState: digitalEngagementInitialState,
    reducers: {
        resetToInitialState(state, action) {
            Object.assign(state, digitalEngagementInitialState);
        },
        setDigitalPhysicianEngagementChannel: (state, action) => {
            state.clickEngagement.selectedChannel = action?.payload;
        },
        setDigitalPhysicianEngagementData: (state, action) => {
            const processedData = processDataForClickEngagement(action.payload, state?.clickEngagement?.actualData || {});
            state.clickEngagement.series = processedData;
            state.clickEngagement.yMax = calculateYmax(processedData,state.clickEngagement.selectedChannel);
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        handleSearch: (state, action) => {
            // Server-side search now. The provider list is one page, so filtering
            // it here would only ever search the rows already on screen. Record
            // the term and reset to page 1; the component re-fetches, and the API
            // returns the matching page plus a `providers_total` for the pager.
            const { searchTerm } = action.payload;
            state.searchTerm = searchTerm ?? '';
            state.currentPage = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            // Provider table page/search. Updates ONLY the table slice -- the
            // graphs, counters and distributions are left exactly as the last
            // full dashboard load set them.
            .addCase(getProviderPage.pending, (state) => {
                state.isProvidersLoading = true;
            })
            .addCase(getProviderPage.fulfilled, (state, action) => {
                state.isProvidersLoading = false;
                const rows = action.payload.engagement?.fmt_provider ?? [];
                const total = action.payload.engagement?.providers_total ?? rows.length;
                state.providers = rows;
                state.filteredProviders = rows;
                state.serverPaginated = true;
                state.totalRecordsFetch = total;
                state.totalPages = Math.ceil(total / (state.pageSize || 1));
            })
            .addCase(getProviderPage.rejected, (state) => {
                state.isProvidersLoading = false;
            })
            .addCase(getDigitalEngagement.fulfilled, (state, action) => {
                state.isLoading = false;
                state.engagementTotal = action.payload.engagement?.channelEngagement?.digital?.current;
                {
                    const rows = action.payload.engagement?.fmt_provider ?? [];
                    // providers_total is the count of ALL matching providers (or of
                    // the search result), not of this page, so the pager stays
                    // correct when the API sends 10 rows out of 41,905.
                    const total = action.payload.engagement?.providers_total;
                    state.providers = rows;
                    state.filteredProviders = rows;
                    state.serverPaginated =
                        total !== undefined && total !== null && total > rows.length;
                    state.totalRecordsFetch = total ?? rows.length;
                    state.totalPages = Math.ceil(
                        (total ?? rows.length) / (state.pageSize || 1),
                    );
                }
                /* Click Engagement */
                const newData = removeEmptyNestedKeys(action.payload?.engagement?.clickEngagement || {});
                state.clickEngagement.actualData = newData;
                state.clickEngagement.series = processDataForClickEngagement(state?.clickEngagement?.selectedChannel, newData || {});
                state.clickEngagement.yMax = calculateYmax(state.clickEngagement.series,state.clickEngagement.selectedChannel);
                state.clickEngagement.labels = makeLabels(newData, true, true,false,true);
                state.clickEngagement.categoriesForAll = makeLabels(newData, true, false);
                state.clickEngagement.categories = makeLabels(newData);
                // map chart 
                state.overallPhysicians = getOverallPhysicians(action.payload.engagement?.regionalEngagement || []);
                state.stateOverallPhysicians = getOverallPhysicians(action.payload.engagement?.stateEngagement || []);
                state.districtBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.engagement?.regionalEngagement || [] ) ;
                state.stateBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.engagement?.stateEngagement || [] ) ;
                state.stateMapSeries = createStateUSAMapData(action.payload?.engagement?.stateEngagement || []);
                // territories map
                state.overallTerritoriesPhysicians = getOverallPhysicians(action.payload.engagement?.territoryEngagement || []);
                state.distributionTerritories = makeBarDataSeries(action.payload?.engagement?.territoryEngagement || []) || [];
                state.territoriesBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.engagement?.territoryEngagement || [] ) ;
                /* Period Change */ 
                const { period, isPeriodChangeIncrease } = handlePeriodChange(action.payload.engagement?.channelEngagement?.digital?.current, action.payload.engagement?.channelEngagement?.digital?.previous);
                state.periodChange = period;
                state.isPeriodChangeIncrease = isPeriodChangeIncrease;
                const combinedData = action?.payload?.engagement?.combined_segment || {};
                state.physicianEngagementSegmentsDonutChartData = makephysicianEngagementSegmentsDonutChartData(action.payload.engagement?.segmentEngagement || {},combinedData, true);
                state.physicianEngagementAffiliationTypeDonutChartData = makephysicianEngagementSegmentsDonutChartData(action.payload?.engagement?.affiliationTypeSegments || {}, {}, true);

                /* Specialty Engagement */ 
                if(action.payload?.engagement?.specialtyEngagement && Object.entries(action.payload?.engagement?.specialtyEngagement).length > 0){
                    state.specialtyEngagement = makephysicianEngagementSegmentsDonutChartData(action.payload?.engagement?.specialtyEngagement || []);
                    state.specialtyEngagementData = processDataForSpecialty(action.payload.engagement.specialtyEngagement ?? null);
                }

                /* Decile Engagement */ 
                if(action.payload?.engagement?.decileEngagement && Object.entries(action.payload?.engagement?.decileEngagement).length > 0){
                    state.decileEngagement = makephysicianEngagementSegmentsDonutChartData(action.payload?.engagement?.decileEngagement || []);
                    state.decileEngagementData = processData(action.payload.engagement.decileEngagement ?? null);
                }
                /* Physician Engaged bar chart */
                if(action.payload?.engagement?.decilePercentage && Object.entries(action.payload?.engagement?.decilePercentage).length > 0){
                    const { decilePercentage } = action.payload.engagement;
                    const decilePercentagecategories = [];
                    const decilePercentageData = [];
                    for (const [key, value] of Object.entries(decilePercentage)) {
                        if (!isNaN(parseInt(key))) {
                            decilePercentagecategories.push(key);
                            decilePercentageData.push(Math.ceil(value));
                        }
                    }
                    state.physicianEngagedBarChart.categories = decilePercentagecategories || [];
                    state.physicianEngagedBarChart.data = decilePercentageData || [];
                }
                // hospital chart starts
                const hospitalData = action.payload?.engagement?.hospitalEngagement || [];
                state.hospitalEngagementData = hospitalData || [];
                state.hospitalBarChart = transformHospitalDataForBarChart(hospitalData,false,true,'all') || [];
                state.hospitalMapChart = transformHospitalDataForMapChart(hospitalData, 'all');
                state.hospitalStateBarChart = transformHospitalDataForBarChart(hospitalData,true,false,'all') || [];
                state.overallHospitalPhysicians = getOverallPhysicians(hospitalData || []);
                // ends
                // distribution map chart bar data
                state.stateDistributionRegion = makeBarDataSeries(action.payload?.engagement?.stateEngagement,false,true) || [];
                state.distributionRegion = makeBarDataSeries(action.payload?.engagement?.regionalEngagement || []) || [];
                state.stateDistributionRegionPercentage = makeBarDataSeries(action.payload?.engagement?.stateEngagementPercentage,false,true,
                    false,false,false, true) || [];
                state.distributionRegionPercentage = makeBarDataSeries(action.payload?.engagement?.districtEngagementPercentage || [], 
                    false,false,false,false,false, true) || [];
                state.clickEngagement.colors = colors;
                state.engagement = { ...action.payload.engagement, segmentEngagement: action.payload?.engagement?.segmentEngagement };
                state.regionalEngagement = action.payload.engagement?.regionalEngagement;
                state.stateEngagement = action.payload.engagement?.stateEngagement;
                //No. of Engagements
                const repeatEngagementData = action.payload?.engagement?.repeatEngagementDistribution || [];
                let data = transformEngagementData(repeatEngagementData || []);
                const npiEngagedData = [];
                // let data = action.payload?.engagement?.engagedEngagementDistribution || []; // need to uncomment this line
                data = data.filter(item=>item.count > 0);
                data.map(item=>{
                    npiEngagedData.push(
                        {
                            name: item.label,
                            y: item.count
                        }
                    );
                });
                // repeat engagement data
                const repeatEngagement = transformForStackedBarChart(repeatEngagementData);
                state.repeatEngagementBarChart.categories = repeatEngagement.categories || [];
                state.repeatEngagementBarChart.data = repeatEngagement.series || [];
                state.NpiEngagedChart = npiEngagedData || [];
                // HCP reach vs engagement (targetlist_hcps)
                state.hcpReachEngagement = makeHcpReachEngagementData(action.payload?.engagement?.targetlist_hcps);
                // new users vs returning users
                const sortedNewUserData = sortDataByMonth(action.payload?.engagement?.new_users_chart_data|| []);
                const sortedReturningData =sortDataByMonth(action.payload?.engagement?.returning_users_chart_data || []);
                const usersAreaData = createUsersAreaChartSeries(sortedNewUserData,sortedReturningData);
                state.users_series_data = usersAreaData;
                state.error = null;
            })
            .addCase(getDigitalEngagement.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getDigitalEngagement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    }
});

const makeHcpReachEngagementData = (data) => {
    if (!Array.isArray(data)) return [];
    const toNumber = (val) => {
        const num = Number(val);
        return Number.isFinite(num) && num > 0 ? num : 0;
    };
    return data
        .filter((item) => item && typeof item === 'object' && item.month && item.segment)
        .map((item) => ({
            month: String(item.month),
            segment: String(item.segment),
            totalTargetHCPs: toNumber(item.totalTargetHCPs),
            reach: toNumber(item.reach),
            successfullyEngaged: toNumber(item.successfullyEngaged),
            reachPercentage: toNumber(item.reachPercentage),
            successfullyEngagedPercentage: toNumber(item.successfullyEngagedPercentage)
        }));
};

const makeSeriesForSpecialty = (data) => {
    let series = [], sortable = [], sortedObj = {};
    if(data && Object.entries(data).length > 0){
        if(Object.entries(data).length > 15){
            let othersSum = 0, others= [];
            sortable = Object.entries(data);
            
            sortable.sort((a, b) => b[1] - a[1]); 
            
            others = sortable.slice(14); 
            
            others.forEach(item => othersSum += item[1]); 
            sortable = sortable.slice(0, 14); 
            sortable.push([
                'Others', 
                Math.round(othersSum *100) /100
            ]); 
            sortedObj = Object.fromEntries(sortable);
        } else {
            sortable = Object.entries(data);
            
            sortable.sort((a, b) => b[1] - a[1]); 
            sortedObj = Object.fromEntries(sortable);
        }
    }

    for (const category in sortedObj) {
        if (sortedObj[category]) {
            series.push(Number(sortedObj[category]));
        }

    }

    return series;
};

const makeLabelsForSpecialty = (data) => {
    let labels = [], sortable = [], sortedObj = {};
    
    if(data && Object.entries(data).length > 0){
        if(Object.entries(data).length > 15){
            let othersSum = 0, others= [];
            sortable = Object.entries(data);
            sortable.sort((a, b) => b[1] - a[1]); 
            others = sortable.slice(14); 
            others.forEach(item => othersSum += item[1]); 
            sortable = sortable.slice(0, 14); 
            sortable.push([
                'Others', 
                Math.round(othersSum *100) /100
            ]); 
            sortedObj = Object.fromEntries(sortable);
        } else {
            sortable = Object.entries(data);
            
            sortable.sort((a, b) => b[1] - a[1]); 
            sortedObj = Object.fromEntries(sortable);
        }
    }
    
    for (const category in sortedObj) {
        labels.push(category);
    }
    
    return labels;
};

const processDataForSpecialty = (data) => {
    const labels = makeLabelsForSpecialty(data);
    const series = makeSeriesForSpecialty(data);

    return { labels, series };
};

const processData = (data, key) => {
    const labels = makeLabels(data);
    const series = makeSeriesForDonutChart(data, key);

    return { labels, series };
};

const makephysicianEngagementSegmentsDonutChartData = (a, b, isSort = false) => {
    const fallbackB = b || {}; // fallback if b is null/undefined

    const result = Object.entries(a).map(([name, value]) => {
        // Case-insensitive match key from b
        const lookupKey = Object.keys(fallbackB).find(
            bKey => bKey.toLowerCase() === name.toLowerCase()
        );

        const engagementData = lookupKey ? fallbackB[lookupKey] : {};

        return {
            name,
            y: parseFloat(value),
            ...(engagementData?.engaged !== undefined && { engaged: engagementData.engaged }),
            ...(engagementData?.non_engaged !== undefined && { non_engaged: engagementData.non_engaged })
        };
    });

    // Sort by y descending if isSort is true
    return isSort
        ? result.sort((a, b) => a.name.localeCompare(b.name))
        : result;
};


const makeColors = (data) => generateDistinctHexColors(data?.length || 0);

const processDataForClickEngagement = (selectedChannel, channelData) => {
    let data = [];
    const labels = makeLabels(channelData);

    if (selectedChannel?.toLowerCase() === 'all') {
        let channelValues = Object?.entries(channelData)?.map(([channel, value]) => ({
            name: channel,
            value: Object?.values(value)?.reduce((acc, val) => acc + val, 0)
        }));
        channelValues = channelValues?.filter(channel => channel.value !== 0);

        channelValues?.sort((a, b) => b.value - a.value);

        data = channelValues?.map(channel => ({
            name: channel.name,
            data: Object?.values(channelData[channel.name])
        }));

        return data;
    } else {
        data = Object?.entries(channelData)?.map((item, index ) => {
            const [channel, value] = item;
            return {
                x: channel,
                y: value[selectedChannel] || 0,
                fillColor: colors[index] 
            };
        });
        return [
            {
                name: selectedChannel,
                data: data
            }
        ];
    }
};

function transformForStackedBarChart(data) {
    // Sort by month
    const sortedData = [...data].sort((a, b) => a.month.localeCompare(b.month));
  
    // Extract and format months
    const rawCategories = sortedData.map(item => item.month);
    const categories = customCategories(rawCategories); // your formatter function
  
    // Collect all unique engagement keys across all months
    const allKeys = new Set();
    sortedData.forEach(entry => {
        Object.keys(entry).forEach(key => {
            if (key !== 'month') {
                allKeys.add(key);
            }
        });
    });
  
    // Build the final series
    const series = [...allKeys].map(key => ({
        name: key,
        data: sortedData.map(entry => entry[key] ?? 0)
    }));
    return { series, categories };
}
function transformEngagementData(data) {
    const counts = {};
  
    // Aggregate the counts
    data.forEach(entry => {
        Object.entries(entry).forEach(([key, value]) => {
            if (key === 'month') return;
            const baseLabel = key.match(/^Engaged #[^ ]+/)[0]; // e.g., "Engaged #1"
            counts[baseLabel] = (counts[baseLabel] || 0) + value;
        });
    });
  
    // Total count for percentage calculation
    const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
  
    // Build the result array
    return Object.entries(counts).map(([baseLabel, count]) => {
        const percentage = ((count / total) * 100).toFixed(1);
        return {
            label: `${baseLabel} (${percentage}%)`,
            count
        };
    });
}


export const { actions } = digitalEngagement;
export default digitalEngagement.reducer;